import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import AddNoteMutation from './AddNoteMutation';
import GetParticipantDataQuery from '../GetParticipantDataQuery';
import UI from '../../../../common/UI';
import useMutation from '../../../../api/useMutation';
import useValidation from '../../../../common/useValidation';

const addNoteSchema = yup.object().shape({
  content: yup.string().required(),
});

const useAddNoteModal = (participantId: string, refetchQueries?: DocumentNode[]) => {
  const [openModal, closeModal] = useModal(
    () => (
      <UI.Modal maxWidth={600} close={closeModal}>
        <AddNoteForm participantId={participantId} onClose={() => closeModal()} refetchQueries={refetchQueries} />
      </UI.Modal>
    ),
    [participantId, refetchQueries],
  );

  return [openModal, closeModal];
};

export interface AddNoteFormProps {
  participantId: string;
  onClose: () => void;
  refetchQueries?: DocumentNode[];
}

/**
 * Render form with a textarea for adding a note to a participant.
 */
export const AddNoteForm = ({
  participantId, onClose, refetchQueries = [GetParticipantDataQuery],
}: AddNoteFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const form = useForm({
    values: {
      content: '',
    },
  });

  const [addNote, { loading, error }] = useMutation(
    AddNoteMutation,
    {
      variables: {
        input: {
          participant: { id: participantId },
          content: form.values.content,
        },
      },
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        notifier.success(t('notifications.note_added'));
        onClose?.();
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: addNoteSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => addNote()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4 sc={{ mb: 2 }}>{t('add_a_note')}</UI.H4>
          <UI.Paragraph sc={{ mb: 0 }}>
            {t('add_note_description')}
          </UI.Paragraph>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.FormGrid>
          <UI.InputGroup sc={{ valid: !errors.content, touched: form.getTouched('content') }}>
            <UI.InputLabel htmlFor="note_content">
              {t('note')}
            </UI.InputLabel>
            <UI.TextArea
              {...form.textarea('content')}
              autoFocus
              id="note_content"
            />
            <UI.ErrorMessages attribute={t('content')} errors={errors.content} />
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('add_note')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default useAddNoteModal;
